import type {
  AgentInfo,
  Session,
  Message,
  ModelRef,
  FileNode,
  FileSystemEntry,
  Provider,
  Model,
  Permission,
  Question,
} from "./opencodeTypes"
import type { PromptFile } from "./attachments"
import { companyStore, tokenStore } from "./api"
import { eventSessionId } from "./sessionEvents"
import {
  fromModelRef,
  messageFromWithParts,
  toAgent,
  toModels,
  toPermission,
  toProviders,
  toSession,
  type WireAgent,
  type WirePermission,
  type WireProviderList,
  type WireSession,
  type WireWithParts,
} from "./v1"

export interface CommandInfo {
  name: string
  template: string
  description?: string
  agent?: string
  model?: { providerID: string; id: string }
  subtask?: boolean
}

// The container's HTTP API, reached through the cloud proxy.
//
// Two surfaces, and the split is deliberate. The chat — sessions, messages,
// the event stream, permissions, questions, agents, models — speaks the
// agent's v1 API: `/session`, `/event`, `/permission`, `/question`, `/agent`,
// `/provider`. That is the surface the CLI, the TUI and the desktop app use.
// The v2 "/api/session" surface it replaced is built on a session runner that
// is unfinished upstream, and the concrete cost was that its tool list never
// includes MCP servers: the container declared the board tools, cached all of
// them, and the model got none. Everything else — the terminal (`/api/pty`)
// and the file browser (`/api/fs`) — stays on `/api`, where it works.
//
// v1 answers with the record itself; v2 wrapped everything in `{ data }`.
// The wire shapes and their mapping onto what the components render are in
// lib/v1.ts.
export class OpenCodeApi {
  readonly baseUrl: string
  private authHeader: string
  private useProxy: boolean

  // What the composer picked, per session. The v1 surface has no "set the
  // session's model" call — the choice rides on each prompt — so the client
  // remembers it and sends it every time. A reload starts from what answered
  // last (see lastSwitchedModel in lib/sessionEvents.ts), which is the same
  // thing once a prompt has gone through.
  private readonly selectedModel = new Map<string, ModelRef>()
  private readonly selectedAgent = new Map<string, string>()

  constructor(baseUrl: string, username: string, password: string, useProxy = false) {
    this.baseUrl = baseUrl.replace(/\/$/, "")
    this.authHeader = `Basic ${btoa(`${username}:${password}`)}`
    this.useProxy = useProxy
  }

  // The proxy authenticates the browser with the cloud JWT and swaps in the
  // container's basic auth itself. Every request out of this client must go
  // through here — a call that hardcodes this.authHeader is rejected 401 by
  // the cloud's authMiddleware, which only accepts `Bearer`.
  private authorization(): string | undefined {
    if (!this.useProxy) return this.authHeader
    const token = tokenStore.get()
    if (token) return `Bearer ${token}`
    console.warn("[OpenCodeApi] No JWT token found in localStorage for proxy mode")
    return undefined
  }

  // The company this browser acts for. Without it the server cannot tell which
  // of several companies is meant, and the proxy's tenant check refuses a
  // session that belongs to any other — the chat 404ed for exactly the people
  // who work across companies. Same header the main client sends.
  private companyHeader(): Record<string, string> {
    if (!this.useProxy) return {}
    const company = companyStore.get()
    return company ? { "x-moderniser-company": company } : {}
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    }

    const authorization = this.authorization()
    if (authorization) {
      ;(headers as Record<string, string>).Authorization = authorization
    }
    for (const [name, value] of Object.entries(this.companyHeader())) {
      ;(headers as Record<string, string>)[name] = value
    }

    // Merge with any additional headers from init
    if (init.headers) {
      Object.assign(headers, init.headers)
    }

    const url = `${this.baseUrl}${path}`
    console.log(`[OpenCodeApi] ${init.method || "GET"} ${url}`)

    try {
      const res = await fetch(url, {
        ...init,
        headers,
      })

      console.log(`[OpenCodeApi] Response status: ${res.status}, content-type: ${res.headers.get("content-type")}`)

      if (!res.ok) {
        const text = await res.text().catch(() => "")
        console.error(`[OpenCodeApi] Error ${res.status}: ${text}`)
        throw new Error(`OpenCode API error: ${res.status} ${text}`)
      }
      if (res.status === 204) return undefined as T

      const text = await res.text()
      console.log(`[OpenCodeApi] Response body length: ${text.length} bytes`)

      if (text.length > 0) {
        console.log(`[OpenCodeApi] Response preview: ${text.substring(0, 200)}`)
      }

      if (text.length === 0) return undefined as T

      try {
        const data = JSON.parse(text)
        return data
      } catch (parseError) {
        console.error(`[OpenCodeApi] Failed to parse JSON:`, parseError)
        console.error(`[OpenCodeApi] Raw response:`, text)
        throw new Error(
          `Failed to parse response: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
        )
      }
    } catch (error) {
      console.error(`[OpenCodeApi] Fetch error for ${url}:`, error)
      throw error
    }
  }

  async testConnection(): Promise<boolean> {
    try {
      await this.request("/api/health")
      return true
    } catch {
      return false
    }
  }

  // --- Sessions ---

  // Roots only: the agent creates a child session per sub-agent it delegates
  // to, and the chat is the conversation, not the delegations. The filter in
  // lib/chatSession.ts still applies for a server that ignores the query.
  async listSessions(): Promise<Session[]> {
    const result = await this.request<WireSession[]>("/session?roots=true")
    return (result ?? []).map(toSession)
  }

  async createSession(title?: string): Promise<Session> {
    const result = await this.request<WireSession>("/session", {
      method: "POST",
      body: JSON.stringify(title ? { title } : {}),
    })
    return toSession(result)
  }

  // Oldest first, already — v1 pages newest-first internally and reverses
  // before answering (Session.messages in the agent), so no reversal here.
  // Reversing again is what would put the transcript upside down.
  async getMessages(sessionId: string): Promise<Message[]> {
    const result = await this.request<WireWithParts[]>(`/session/${sessionId}/message`)
    return (result ?? []).map(messageFromWithParts)
  }

  // Accepted, not answered: `prompt_async` returns the moment the prompt is
  // queued, and the reply arrives over the event stream like everything else.
  // The synchronous `/message` variant holds the request open for the whole
  // turn, which would keep the composer disabled until the agent finished.
  //
  // `files` carries image attachments as data URIs; text attachments are
  // already inlined into `text` (see lib/attachments.ts). The model and agent
  // are whatever the composer last picked, or the session's defaults when it
  // never did — omitted rather than guessed, so the agent's own config decides.
  async sendMessage(
    sessionId: string,
    text: string,
    files?: PromptFile[],
    model?: ModelRef,
    agent?: string,
  ): Promise<void> {
    const ref = model ?? this.selectedModel.get(sessionId)
    const chosenAgent = agent ?? this.selectedAgent.get(sessionId)
    const parts: Array<Record<string, unknown>> = [{ type: "text", text }]
    for (const file of files ?? []) {
      parts.push({ type: "file", mime: mimeOf(file.uri), url: file.uri, filename: file.name })
    }
    await this.request(`/session/${sessionId}/prompt_async`, {
      method: "POST",
      body: JSON.stringify({
        parts,
        ...(ref ? { model: fromModelRef(ref) } : {}),
        ...(ref?.variant ? { variant: ref.variant } : {}),
        ...(chosenAgent ? { agent: chosenAgent } : {}),
      }),
    })
  }

  // Remembered here and sent with the next prompt; nothing to tell the server
  // until then. Resolves immediately, so the composer's pending state clears
  // when the transcript shows a reply from this model — same contract as
  // before, different mechanism.
  async switchModel(sessionId: string, model: ModelRef): Promise<void> {
    this.selectedModel.set(sessionId, model)
  }

  async switchAgent(sessionId: string, agent: string): Promise<void> {
    this.selectedAgent.set(sessionId, agent)
  }

  // Takes effect from the next step. Aborting an idle session is a no-op on the
  // server, so this is always safe to call.
  async interrupt(sessionId: string): Promise<void> {
    await this.request(`/session/${sessionId}/abort`, { method: "POST" })
  }

  // Whether a turn is running *right now*. The event stream only reports
  // transitions, so a page that loads mid-turn has no other way to know —
  // without this, the Working indicator and the Stop button appear only from
  // the next step boundary onwards. A retry is a turn that is still running.
  async isActive(sessionId: string): Promise<boolean> {
    const result = await this.request<Record<string, { type?: string }>>("/session/status")
    const type = result?.[sessionId]?.type
    return type === "busy" || type === "retry"
  }

  async listAgents(): Promise<AgentInfo[]> {
    const result = await this.request<WireAgent[]>("/agent")
    return (result ?? []).map(toAgent)
  }

  async listProviders(): Promise<Provider[]> {
    const result = await this.request<WireProviderList>("/provider")
    return toProviders(result)
  }

  async listModels(): Promise<Model[]> {
    const result = await this.request<WireProviderList>("/provider")
    return toModels(result)
  }

  // --- Files (v2 `/api/fs`, unchanged) ---

  // Omitting `path` lists the project root — the agent resolves it and reports
  // which directory that was in `location.directory`.
  async listFiles(path?: string): Promise<FileNode[]> {
    const query = path ? `?path=${encodeURIComponent(path)}` : ""
    const result = await this.request<{ data: FileSystemEntry[] }>(`/api/fs/list${query}`)
    return (result.data || []).map((entry) => ({
      ...entry,
      // Trailing slash first: directories arrive as "src/", so splitting without
      // stripping gives every one of them an empty name.
      name: entry.path.replace(/\/+$/, "").split("/").pop() || entry.path,
    }))
  }

  // Deliberately not using request(): this endpoint answers with the file itself
  // as `application/octet-stream`, and request() ends in JSON.parse, which threw
  // on every file that was not coincidentally valid JSON. That was the whole of
  // "files cannot be opened".
  //
  // The path goes in the URL path, against a `*` wildcard route. Encoding it
  // whole turns "src/a.ts" into "src%2Fa.ts"; the agent happens to accept both,
  // but sending the separators as separators is the honest form. Segments are
  // still encoded individually so a name with a space or a hash survives.
  async readFile(path: string): Promise<{ content: string }> {
    const encoded = path
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/")

    const headers: Record<string, string> = {}
    const authorization = this.authorization()
    if (authorization) headers.Authorization = authorization
    Object.assign(headers, this.companyHeader())

    const res = await fetch(`${this.baseUrl}/api/fs/read/${encoded}`, { headers })
    if (!res.ok) {
      const text = await res.text().catch(() => "")
      throw new Error(`OpenCode API error: ${res.status} ${text}`)
    }
    return { content: await res.text() }
  }

  // v2 file search — for @ autocomplete.
  async findFiles(query: string, type?: "file" | "directory", limit?: number): Promise<FileSystemEntry[]> {
    const params = new URLSearchParams({ query })
    if (type) params.set("type", type)
    if (limit) params.set("limit", String(limit))
    const result = await this.request<{ data: FileSystemEntry[] }>(`/api/fs/find?${params.toString()}`)
    return result.data
  }

  // --- Artifacts (v2 `/api/fs` with a location override) ---

  // Where the entrypoint puts deliverables, outside the repo clone. The
  // entrypoint must agree — keep the two in step, and change both together.
  static readonly ARTIFACTS_DIRECTORY = "/workspace/artifacts"

  // The container resolves every fs request against a directory that can be
  // overridden per request — `?location[directory]=` (server/src/location.ts)
  // — and the cloud proxy forwards query strings untouched, so listing the
  // artifacts folder is the same call listFiles() makes, pointed elsewhere.
  async listArtifacts(): Promise<FileNode[]> {
    const params = new URLSearchParams({ "location[directory]": OpenCodeApi.ARTIFACTS_DIRECTORY })
    const result = await this.request<{ data: FileSystemEntry[] }>(`/api/fs/list?${params.toString()}`)
    return (result.data || []).map((entry) => ({
      ...entry,
      name: entry.path.replace(/\/+$/, "").split("/").pop() || entry.path,
    }))
  }

  // Not request(), for the same reason readFile() cannot be: the response is
  // the file itself as raw bytes, and request() ends in JSON.parse — binary
  // artifacts (xlsx, pptx) would throw or corrupt. Blob, so the download keeps
  // every byte; readFile()'s res.text() is lossy the moment a UTF-8 decoder
  // touches a spreadsheet.
  async readArtifact(path: string): Promise<Blob> {
    const encoded = path
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/")

    const params = new URLSearchParams({ "location[directory]": OpenCodeApi.ARTIFACTS_DIRECTORY })
    const headers: Record<string, string> = {}
    const authorization = this.authorization()
    if (authorization) headers.Authorization = authorization
    Object.assign(headers, this.companyHeader())

    const res = await fetch(`${this.baseUrl}/api/fs/read/${encoded}?${params.toString()}`, { headers })
    if (!res.ok) {
      const text = await res.text().catch(() => "")
      throw new Error(`OpenCode API error: ${res.status} ${text}`)
    }
    return res.blob()
  }

  // --- Permissions and questions ---
  //
  // Both lists are instance-wide on v1 — every pending request on the
  // container, each carrying its sessionID — so the chat's own are picked out
  // here rather than by the server.

  async listPermissions(sessionId: string, signal?: AbortSignal): Promise<Permission[]> {
    const result = await this.request<WirePermission[]>("/permission", { signal })
    return (result ?? []).filter((request) => request.sessionID === sessionId).map(toPermission)
  }

  // "once" answers this request; "always" also saves the rule for the rest of
  // the session, which is what Remember means.
  async replyToPermission(
    sessionId: string,
    requestId: string,
    approved: boolean,
    remember?: boolean,
  ): Promise<void> {
    void sessionId
    await this.request(`/permission/${requestId}/reply`, {
      method: "POST",
      body: JSON.stringify({ reply: approved ? (remember ? "always" : "once") : "reject" }),
    })
  }

  async listQuestions(sessionId: string, signal?: AbortSignal): Promise<Question[]> {
    const result = await this.request<Question[]>("/question", { signal })
    return (result ?? []).filter((request) => request.sessionID === sessionId)
  }

  // One answer per sub-question, in order, each an array of selected labels —
  // `{ answers: [["Set up & run the app"]] }`. A custom reply is just a label
  // the options did not offer; the agent accepts any string.
  async replyToQuestion(sessionId: string, requestId: string, answers: string[][]): Promise<void> {
    void sessionId
    await this.request(`/question/${requestId}/reply`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    })
  }

  // Dismissing a question rather than answering it. The agent fails the tool
  // call with "The user dismissed this question" and carries on — the only way
  // out of a dialog that otherwise blocks until it is answered.
  async rejectQuestion(sessionId: string, requestId: string): Promise<void> {
    void sessionId
    await this.request(`/question/${requestId}/reject`, { method: "POST" })
  }

  // --- Events ---

  // The live bus, filtered to one session. It carries `message.part.delta`, so
  // it is what renders an answer as it is written. The bus is instance-wide,
  // hence the sessionID filter; events without one (server.connected,
  // server.heartbeat) are dropped here.
  // `onOpen` fires every time the stream opens. Its argument is the retry
  // count: 0 is the first connection, anything higher means the stream dropped
  // and whatever happened in the gap was missed. The bus does not replay, so
  // the caller has to refetch history to catch up.
  subscribeToSessionLive(
    sessionId: string,
    callback: (event: any) => void,
    onOpen?: (attempt: number) => void,
  ): () => void {
    return this.subscribeToGlobal((event) => {
      if (eventSessionId(event) === sessionId) callback(event)
    }, onOpen)
  }

  subscribeToGlobal(callback: (event: any) => void, onOpen?: (attempt: number) => void): () => void {
    const abortController = new AbortController()
    this.subscribeSSE(`/event`, callback, abortController.signal, onOpen)
    return () => abortController.abort()
  }

  // Reconnects until aborted. A single fetch is not enough for a stream meant
  // to live as long as the page: a proxy timeout, a container restart or a
  // laptop waking from sleep all end it, and without this loop the chat went
  // quiet permanently with one line in the console.
  private async subscribeSSE(
    path: string,
    callback: (event: any) => void,
    signal: AbortSignal,
    onOpen?: (attempt: number) => void,
  ): Promise<void> {
    let attempt = 0

    while (!signal.aborted) {
      try {
        const authorization = this.authorization()
        const response = await fetch(`${this.baseUrl}${path}`, {
          headers: {
            ...(authorization ? { Authorization: authorization } : {}),
            ...this.companyHeader(),
          },
          signal,
        })

        if (!response.ok || !response.body) {
          console.error(`[OpenCodeApi] SSE ${path} did not open: ${response.status} ${response.statusText}`)
        } else {
          onOpen?.(attempt)
          attempt = 0

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ""

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split("\n")
            buffer = lines.pop() || ""

            for (const line of lines) {
              // Trailing \r for anything that speaks CRLF; ":" lines are
              // keep-alive comments and carry nothing.
              const trimmed = line.endsWith("\r") ? line.slice(0, -1) : line
              if (!trimmed.startsWith("data: ")) continue
              try {
                // The frame is { id, type, properties }; the reducer reads
                // `data`, so it is renamed once here rather than everywhere.
                const frame = JSON.parse(trimmed.slice(6))
                callback({ id: frame.id, type: frame.type, data: frame.properties ?? frame.data ?? {} })
              } catch {
                // A partial or non-JSON frame is not worth tearing the stream
                // down for.
              }
            }
          }
        }
      } catch (error) {
        if (signal.aborted || (error as Error).name === "AbortError") return
        console.error(`[OpenCodeApi] SSE ${path} dropped, reconnecting:`, error)
      }

      if (signal.aborted) return
      const delay = Math.min(500 * 2 ** attempt, 15000)
      attempt++
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  // --- Terminal (v2 `/api/pty`, unchanged) ---

  // No connectPty() here on purpose. Opening the socket needs a ticket from
  // cloud, and cloud is the only party that can reach the container — so the
  // WebSocket is built in Terminal.tsx where the ticket is fetched, not hidden
  // behind a method that cannot authenticate itself.

  // Resize goes over HTTP, not down the socket. Inbound WebSocket frames are
  // raw keystrokes to the agent, so a JSON control message sent that way is
  // typed into the shell instead of resizing it.
  async resizePty(ptyId: string, cols: number, rows: number): Promise<void> {
    await this.request(`/api/pty/${ptyId}`, {
      method: "PUT",
      body: JSON.stringify({ size: { rows, cols } }),
    })
  }

  async listPty(): Promise<any[]> {
    const result = await this.request<{ data: any[] }>("/api/pty")
    return result.data
  }

  async createPty(title?: string): Promise<any> {
    const result = await this.request<{ data: any }>("/api/pty", {
      method: "POST",
      body: JSON.stringify({ title }),
    })
    return result.data
  }

  async deletePty(ptyId: string): Promise<void> {
    await this.request(`/api/pty/${ptyId}`, { method: "DELETE" })
  }

  // --- Session operations ---

  // Compaction needs to be told which model writes the summary; the agent does
  // not fall back to the session's. The caller passes what the composer has.
  async compact(sessionId: string, model: ModelRef | undefined): Promise<void> {
    const ref = model ?? this.selectedModel.get(sessionId)
    if (!ref) throw new Error("Pick a model before compacting")
    await this.request(`/session/${sessionId}/summarize`, {
      method: "POST",
      body: JSON.stringify(fromModelRef(ref)),
    })
  }

  // Single-call undo to a specific message.
  async revert(sessionId: string, messageID: string): Promise<void> {
    await this.request(`/session/${sessionId}/revert`, {
      method: "POST",
      body: JSON.stringify({ messageID }),
    })
  }

  // Restores all reverted messages.
  async unrevert(sessionId: string): Promise<void> {
    await this.request(`/session/${sessionId}/unrevert`, { method: "POST" })
  }

  // Forks the session at a message (or the end if omitted).
  async fork(sessionId: string, messageID?: string): Promise<Session> {
    const result = await this.request<WireSession>(`/session/${sessionId}/fork`, {
      method: "POST",
      body: messageID ? JSON.stringify({ messageID }) : undefined,
    })
    return toSession(result)
  }

  // Creates a share URL for the session.
  async share(sessionId: string): Promise<Session> {
    const result = await this.request<WireSession>(`/session/${sessionId}/share`, {
      method: "POST",
    })
    return toSession(result)
  }

  // Removes the share.
  async unshare(sessionId: string): Promise<Session> {
    const result = await this.request<WireSession>(`/session/${sessionId}/share`, {
      method: "DELETE",
    })
    return toSession(result)
  }

  // Executes a server-side template command (custom or built-in).
  async sendCommand(sessionId: string, command: string, arguments_: string): Promise<void> {
    const ref = this.selectedModel.get(sessionId)
    const agent = this.selectedAgent.get(sessionId)
    await this.request(`/session/${sessionId}/command`, {
      method: "POST",
      body: JSON.stringify({
        command,
        arguments: arguments_,
        ...(ref ? { model: fromModelRef(ref) } : {}),
        ...(agent ? { agent } : {}),
      }),
    })
  }

  // All registered commands (built-in + custom from config). v1 answers with
  // the list itself.
  async listCommands(): Promise<CommandInfo[]> {
    const result = await this.request<CommandInfo[]>("/command")
    return result ?? []
  }
}

// The media type a data URI declares, which is what the agent wants alongside
// it. "data:image/png;base64,..." → "image/png".
function mimeOf(uri: string): string {
  const match = /^data:([^;,]+)[;,]/.exec(uri)
  return match?.[1] ?? "application/octet-stream"
}
